import 'pages/Optimisation/components/DownloadModal/style.css';

import { ApolloClient } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  Alert,
  AlertSentiments,
  Button,
  ButtonGroup,
  Checkbox,
  Drawer,
  Flexbox,
  Label,
  Loading,
  Option,
  Positions,
  Select,
  Sentiments,
  Sizes,
  TextInput,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  AlertCircle,
  Download,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { DisplayDateRange } from 'components/Charting/types';
import { ShellModal } from 'components/Modal/ShellModal';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { downloadData } from 'pages/Optimisation/components/DownloadModal/downloadData';
import { StyledSingleDatePicker } from 'pages/Optimisation/components/DownloadModal/SingleDatePicker.styled';
import { UPDATE_DAILY_REPORT_SUBSCRIPTION } from 'pages/Optimisation/mutations';
import { GET_DAILY_REPORT_SUBSCRIBED } from 'pages/Optimisation/queries';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'store/store';
import styled from 'styled-components';
import { ApiName } from 'types/apiName';
import { TRADE_TYPE } from 'types/trade';
import { checkOptimisationEmailEnabled } from 'utils/features';
dayjs.extend(weekday);
dayjs.extend(localeData);

const DAY_START = '23:00';

const StyledButtonGroup = styled(ButtonGroup)`
  & label {
    width: 20%;
  }
`;

type AlertError = {
  content: string;
  sentiment: AlertSentiments;
};

enum TimePeriodType {
  Custom = 'Custom',
  Yesterday = 'Yesterday',
  LastWeek = 'Last week',
  LastMonth = 'Last month',
  DailyReport = 'Daily report',
}

export const DownloadModal = ({
  showModal,
  onClose,
  displayDateRange,
}: {
  showModal: boolean;
  onClose: (event?: any) => void;
  displayDateRange: DisplayDateRange;
}) => {
  const { isMobileOrTablet } = useDimensions();
  const { appUserId } = useAppUserDetailsFromJwt();
  const client: ApolloClient = useApolloClient();

  const initialStartDate = displayDateRange.start;
  const initialEndDate = displayDateRange.end;
  const [selectedTimePeriodType, setSelectedTimePeriodType] =
    useState<TimePeriodType>(TimePeriodType.Custom);

  const [fromDate, setFromDate] = useState<Dayjs | null>();
  const [fromTime, setFromTime] = useState<string | undefined>();

  const [toDate, setToDate] = useState<Dayjs | null>();
  const [toTime, setToTime] = useState<string | undefined>();

  const [tradeTypes, setTradeTypes] = useState<TRADE_TYPE[]>([
    TRADE_TYPE.Wholesale,
    TRADE_TYPE.Ancillary,
    TRADE_TYPE.BalancingMechanism,
  ]);

  const [subscribeToEmails, setSubscribeToEmails] = useState<boolean>(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState<boolean>(false);
  const [subscriptionSaved, setSubscriptionSaved] = useState<boolean>(false);

  const isOptimisationEmailEnabled = checkOptimisationEmailEnabled();

  const isDailyReportTab =
    isOptimisationEmailEnabled &&
    selectedTimePeriodType === TimePeriodType.DailyReport;

  const getJsDateFromDayjsAndTime = (
    date: Dayjs | null | undefined,
    time: string | undefined,
  ): Date | null => {
    return date && time
      ? dayjs(date)
          .set('hour', parseInt(time.split(':')[0], 10))
          .set('minute', parseInt(time.split(':')[1], 10))
          .set('second', 0)
          .set('millisecond', 0)
          .toDate()
      : null;
  };

  const { site } = useParams();
  const siteId = Number(site);
  const hasValidSiteId = Number.isFinite(siteId);
  const hasValidAppUserId = Number.isFinite(appUserId);
  const [selectedSite] = useStore((state) => [state.selectedSite]);

  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<AlertError | undefined>();

  const [subscriptionLoading, setSubscriptionLoading] =
    useState<boolean>(false);

  const apolloClient = client;

  const fetchSubscription = useCallback(async () => {
    if (
      !isOptimisationEmailEnabled ||
      !showModal ||
      !hasValidSiteId ||
      !hasValidAppUserId
    )
      return;
    setSubscriptionLoading(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_DAILY_REPORT_SUBSCRIBED,
        variables: { siteId, userId: appUserId! },
        fetchPolicy: 'network-only',
        context: { apiName: ApiName.plutoApi },
      });
      if (data?.isDailyReportSubscribed !== undefined) {
        setSubscribeToEmails(data.isDailyReportSubscribed);
      }
    } catch {
      // Query error handled silently; checkbox stays at current value
    } finally {
      setSubscriptionLoading(false);
    }
  }, [
    apolloClient,
    isOptimisationEmailEnabled,
    showModal,
    hasValidSiteId,
    hasValidAppUserId,
    siteId,
    appUserId,
  ]);

  useEffect(() => {
    setSubscriptionSaved(false);
    setSubscriptionSaving(false);
    setError(undefined);
    fetchSubscription();
  }, [fetchSubscription]);

  const [updateSubscription] = useMutation(UPDATE_DAILY_REPORT_SUBSCRIPTION, {
    context: {
      apiName: ApiName.plutoApi,
    },
  });

  const getErrorDetails = (err: unknown): string => {
    if (err instanceof Error) {
      return err.message;
    }
    if (typeof err === 'string') {
      return err;
    }
    try {
      return JSON.stringify(err);
    } catch (_jsonError) {
      return 'Unknown error';
    }
  };

  const handleSaveSubscription = async () => {
    try {
      if (!hasValidSiteId || !hasValidAppUserId) {
        setError({
          content:
            'Unable to save email preference for the selected user or site.',
          sentiment: Sentiments.Negative,
        });
        return;
      }

      setError(undefined);
      setSubscriptionSaved(false);
      setSubscriptionSaving(true);
      await updateSubscription({
        variables: {
          siteId,
          userId: appUserId,
          isSubscribed: subscribeToEmails,
        },
      });
      setError(undefined);
      setSubscriptionSaved(true);
    } catch (err) {
      const errorDetails = getErrorDetails(err);
      setError({
        content: `Failed to save email preference. Please try again. ${errorDetails}`,
        sentiment: Sentiments.Negative,
      });
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const handleDownload = async () => {
    const fromDateTime = getJsDateFromDayjsAndTime(fromDate, fromTime);
    const toDateTime = getJsDateFromDayjsAndTime(toDate, toTime);

    if (tradeTypes.length === 0 || !fromDateTime || !toDateTime) {
      setError({
        content: 'Please specify all inputs to download data.',
        sentiment: Sentiments.Negative,
      });
      return;
    } else if (fromDateTime > toDateTime) {
      setError({
        content: 'The start date must be earlier than the end date.',
        sentiment: Sentiments.Negative,
      });
      return;
    } else {
      setError(undefined);
    }

    setLoading(true);
    try {
      const dateRange: DisplayDateRange = {
        start: fromDateTime.toISOString(),
        end: toDateTime.toISOString(),
      };
      const dataDownloaded = await downloadData(client, {
        siteId: site,
        siteName: selectedSite,
        dateRange,
        tradeTypes,
      });

      if (dataDownloaded) {
        onClose();
      } else {
        setError({
          content: 'There is no recorded data that matches your selection.',
          sentiment: Sentiments.Information,
        });
      }
    } catch (err) {
      const errorDetails = getErrorDetails(err);
      setError({
        content: `Failed to download data. Please try again. ${errorDetails}`,
        sentiment: Sentiments.Negative,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateDateRange = (start: Dayjs, end: Dayjs) => {
      setFromDate(start);
      setFromTime(DAY_START);
      setToDate(end);
      setToTime(DAY_START);
    };
    const lastMonthStartDate = dayjs()
      .subtract(1, 'month')
      .startOf('month')
      .subtract(1, 'day');
    const lastMonthEndDate = dayjs().subtract(1, 'month').endOf('month');

    const lastWeekStartDate = dayjs().subtract(1, 'week').startOf('week');
    const lastWeekEndDate = dayjs()
      .subtract(1, 'week')
      .endOf('week')
      .add(1, 'day');

    const yesterdayStartDate = dayjs().subtract(2, 'day').endOf('day');
    const yesterdayEndDate = yesterdayStartDate.add(1, 'day');

    if (selectedTimePeriodType === TimePeriodType.LastMonth) {
      updateDateRange(lastMonthStartDate, lastMonthEndDate);
    } else if (selectedTimePeriodType === TimePeriodType.LastWeek) {
      updateDateRange(lastWeekStartDate, lastWeekEndDate);
    } else if (selectedTimePeriodType === TimePeriodType.Yesterday) {
      updateDateRange(yesterdayStartDate, yesterdayEndDate);
    } else if (
      selectedTimePeriodType === TimePeriodType.Custom &&
      initialStartDate &&
      initialEndDate
    ) {
      updateDateRange(dayjs(initialStartDate), dayjs(initialEndDate));
    }
  }, [selectedTimePeriodType, initialStartDate, initialEndDate]);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const downloadContent = (
    <>
      <div style={{ height: '12px' }}></div>
      <StyledButtonGroup
        selectedKey={selectedTimePeriodType}
        type="single"
        size={Sizes.Small}
        onChange={(selectedValue) =>
          setSelectedTimePeriodType(selectedValue as TimePeriodType)
        }
        items={[
          { value: TimePeriodType.Custom, label: TimePeriodType.Custom },
          { value: TimePeriodType.Yesterday, label: TimePeriodType.Yesterday },
          { value: TimePeriodType.LastWeek, label: TimePeriodType.LastWeek },
          { value: TimePeriodType.LastMonth, label: TimePeriodType.LastMonth },
          ...(isOptimisationEmailEnabled
            ? [
                {
                  value: TimePeriodType.DailyReport,
                  label: TimePeriodType.DailyReport,
                },
              ]
            : []),
        ]}
        style={{ justifyContent: 'center', width: '100%' }}
      />
      <div style={{ height: isMobileOrTablet ? '20px' : '5px' }} />

      {isDailyReportTab ? (
        <>
          <div style={{ height: isMobileOrTablet ? '10px' : '5px' }} />
          <Label bold>Daily report delivery</Label>
          <div style={{ height: '10px' }} />
          <Checkbox
            checked={subscribeToEmails}
            label="Email me a daily CSV of yesterday's trades at 08:00"
            onChange={(e) => {
              setSubscribeToEmails(e.target.checked);
              setSubscriptionSaved(false);
            }}
            disabled={subscriptionLoading}
          />
          {subscriptionSaved && (
            <Alert
              size={Sizes.Small}
              sentiment={Sentiments.Positive}
              solidBgColor
              style={{
                minHeight: 'unset',
                fontWeight: '300',
                marginTop: '8px',
              }}
            >
              Email preference saved
            </Alert>
          )}
          {!!error && isDailyReportTab && (
            <Alert
              size={Sizes.Small}
              sentiment={error.sentiment}
              icon={<AlertCircle />}
              solidBgColor
              style={{
                minHeight: 'unset',
                fontWeight: '300',
                marginTop: '8px',
              }}
            >
              {error.content}
            </Alert>
          )}
        </>
      ) : (
        <>
          <Label bold>From</Label>
          {isMobileOrTablet && <div style={{ height: '5px' }} />}
          <Flexbox direction="column">
            <StyledSingleDatePicker
              placeholder="Select date"
              showToday={false}
              disabled={selectedTimePeriodType !== TimePeriodType.Custom}
              value={fromDate}
              onChange={setFromDate}
              onFocus={() => setIsDatePickerOpen(true)}
              onBlur={() => setIsDatePickerOpen(false)}
            />
            <div style={{ width: '12px' }} />
            <TextInput
              className="download-modal-time-input"
              type="time"
              disabled={selectedTimePeriodType !== TimePeriodType.Custom}
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
            />
          </Flexbox>
          <div style={{ height: isMobileOrTablet ? '20px' : '5px' }} />
          <Label bold>To</Label>
          {isMobileOrTablet && <div style={{ height: '5px' }} />}
          <Flexbox direction="column">
            <StyledSingleDatePicker
              placeholder="Select date"
              showToday={false}
              disabled={selectedTimePeriodType !== TimePeriodType.Custom}
              value={toDate}
              onChange={setToDate}
              onFocus={() => setIsDatePickerOpen(true)}
              onBlur={() => setIsDatePickerOpen(false)}
            />
            <div style={{ width: '12px' }} />
            <TextInput
              className="download-modal-time-input"
              type="time"
              disabled={selectedTimePeriodType !== TimePeriodType.Custom}
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
            />
          </Flexbox>
          <div style={{ height: isMobileOrTablet ? '20px' : '5px' }} />
          <Label bold>Include</Label>
          {isMobileOrTablet && <div style={{ height: '5px' }} />}
          <Select
            mode="multiple"
            value={tradeTypes}
            onChange={setTradeTypes}
            allowClear={false}
            style={{ minHeight: 'unset' }}
          >
            <Option value={TRADE_TYPE.Wholesale}>Wholesale</Option>
            <Option value={TRADE_TYPE.Ancillary}>Ancillary</Option>
            <Option value={TRADE_TYPE.BalancingMechanism}>
              Balancing Mechanism
            </Option>
          </Select>
          {!!error ? (
            <>
              {isMobileOrTablet && <div style={{ height: '10px' }} />}
              <Alert
                size={Sizes.Small}
                sentiment={error.sentiment}
                icon={<AlertCircle />}
                solidBgColor
                style={{ minHeight: 'unset', fontWeight: '300' }}
              >
                {error.content}
              </Alert>
            </>
          ) : (
            <div style={{ height: isMobileOrTablet ? '20px' : '5px' }} />
          )}
          <div style={{ height: '40px' }} />
        </>
      )}
    </>
  );

  const headerTitle = 'Download trades';
  const headerDescription = 'Please choose what to include in your .csv file.';

  return isMobileOrTablet ? (
    <Drawer
      data-testid="mobile-company-drawer"
      header={{ title: headerTitle, description: headerDescription }}
      size={Sizes.Small}
      open={showModal}
      onClose={onClose}
      placement={Positions.Bottom}
      mask
      elevation={true}
      padding={true}
      style={{
        zIndex: '999',
        marginBottom: '70px',
        maxHeight: '77.5vh',
        padding: '20px',
      }}
    >
      {downloadContent}
      <div style={{ height: '20px' }} />
      <Flexbox direction="column" style={{ placeContent: 'flex-end' }}>
        <Button
          variant={Variants.Outlined}
          sentiment={Sentiments.Primary}
          onClick={onClose}
        >
          Cancel
        </Button>
        <div style={{ width: '5px' }} />
        {isDailyReportTab ? (
          <Button
            variant={Variants.Filled}
            sentiment={Sentiments.Primary}
            onClick={handleSaveSubscription}
            disabled={subscriptionSaving}
          >
            {subscriptionSaving ? <Loading /> : 'Save'}
          </Button>
        ) : (
          <Button
            icon={<Download />}
            variant={Variants.Filled}
            sentiment={Sentiments.Primary}
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? <Loading /> : 'Download'}
          </Button>
        )}
      </Flexbox>
      {isDatePickerOpen && isMobileOrTablet && (
        <div style={{ height: '200px' }}></div>
      )}
    </Drawer>
  ) : (
    <ShellModal
      title={headerTitle}
      headerDescription={headerDescription}
      onClose={onClose}
      open={showModal}
      scrollable
      actions={
        isDailyReportTab
          ? [
              {
                label: 'Cancel',
                props: {
                  variant: Variants.Outlined,
                  sentiment: Sentiments.Primary,
                },
                action: onClose,
              },
              {
                label: subscriptionSaving ? 'Saving...' : 'Save',
                props: {
                  variant: Variants.Filled,
                  sentiment: Sentiments.Primary,
                  loading: subscriptionSaving,
                },
                action: handleSaveSubscription,
              },
            ]
          : [
              {
                label: 'Cancel',
                props: {
                  variant: Variants.Outlined,
                  sentiment: Sentiments.Primary,
                },
                action: onClose,
              },
              {
                label: 'Download',
                props: {
                  variant: Variants.Filled,
                  sentiment: Sentiments.Primary,
                  icon: <Download />,
                },
                action: handleDownload,
              },
            ]
      }
      showFooter
      loading={loading}
      size={Sizes.Large}
      minWidth={600}
      maxWidth={600}
    >
      {downloadContent}
    </ShellModal>
  );
};
